import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navigation from "../components/Navigation";

const StockDetailScreen = () => {
  const navigate = useNavigate();
  const { symbol } = useParams();
  const [timeFrame, setTimeFrame] = useState("1M");
  const [loading, setLoading] = useState(false);

  // Mock stock data
  const stockData = {
    symbol: symbol,
    name:
      symbol === "AAPL"
        ? "Apple Inc."
        : symbol === "TSLA"
        ? "Tesla Inc."
        : symbol === "MSFT"
        ? "Microsoft Corp."
        : symbol === "GOOGL"
        ? "Alphabet Inc."
        : "Company Name",
    currentPrice: "$150.23",
    change: "+$2.45",
    changePercent: "+1.66%",
    marketCap: "$2.34T",
    volume: "45.2M",
    pe: "28.5",
    dividend: "0.88%",
    high: "$152.45",
    low: "$147.89",
    open: "$148.90",
  };

  const timeFrames = ["1D", "5D", "1M", "3M", "6M", "1Y", "5Y", "MAX"];

  const companyInfo = {
    sector: "Technology",
    industry: "Consumer Electronics",
    employees: "164,000",
    founded: "1976",
    headquarters: "Cupertino, CA",
    website: "www.apple.com",
    description:
      "Apple Inc. designs, manufactures, and markets smartphones, personal computers, tablets, wearables, and accessories worldwide.",
  };

  const keyMetrics = [
    { label: "Market Cap", value: stockData.marketCap },
    { label: "P/E Ratio", value: stockData.pe },
    { label: "Dividend Yield", value: stockData.dividend },
    { label: "Volume", value: stockData.volume },
    { label: "52W High", value: stockData.high },
    { label: "52W Low", value: stockData.low },
  ];

  return (
    <div className="screen">
      {/* Header with back button */}
      <div className="card">
        <div className="card-header">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-outline btn-small"
          >
            ← Back
          </button>
          <div>
            <h2 className="card-title">{stockData.name}</h2>
            <div className="card-subtitle">{stockData.symbol}</div>
          </div>
        </div>
      </div>

      {/* Price Information */}
      <div className="card">
        <div className="text-center">
          <div
            style={{
              fontSize: "3rem",
              fontWeight: "bold",
              color: "#E5E7EB",
              marginBottom: "8px",
            }}
          >
            {stockData.currentPrice}
          </div>
          <div
            className={`stock-change ${
              stockData.changePercent.startsWith("+") ? "positive" : "negative"
            }`}
            style={{ fontSize: "1.2rem" }}
          >
            {stockData.change} ({stockData.changePercent})
          </div>
        </div>
      </div>

      {/* Chart Placeholder */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Price Chart</h3>
          <div className="flex gap-2">
            {timeFrames.map((frame) => (
              <button
                key={frame}
                onClick={() => setTimeFrame(frame)}
                className={`btn ${
                  timeFrame === frame ? "" : "btn-outline"
                } btn-small`}
              >
                {frame}
              </button>
            ))}
          </div>
        </div>
        <div
          style={{
            height: "400px",
            background: "#2A2A2A",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#94A3B8",
          }}
        >
          📈 Price chart for {stockData.symbol} ({timeFrame}) will be displayed
          here
        </div>
      </div>

      {/* Key Metrics */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Key Metrics</h3>
        </div>
        <div className="grid-3">
          {keyMetrics.map((metric, index) => (
            <div key={index} className="portfolio-card">
              <div className="portfolio-value">{metric.value}</div>
              <div className="portfolio-label">{metric.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Company Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Company Information</h3>
        </div>
        <div className="grid-2">
          <div>
            <h4 style={{ color: "#E5E7EB", marginBottom: "12px" }}>
              Company Details
            </h4>
            <div style={{ color: "#94A3B8", lineHeight: "1.6" }}>
              <div>
                <strong>Sector:</strong> {companyInfo.sector}
              </div>
              <div>
                <strong>Industry:</strong> {companyInfo.industry}
              </div>
              <div>
                <strong>Employees:</strong> {companyInfo.employees}
              </div>
              <div>
                <strong>Founded:</strong> {companyInfo.founded}
              </div>
              <div>
                <strong>Headquarters:</strong> {companyInfo.headquarters}
              </div>
              <div>
                <strong>Website:</strong>{" "}
                <a
                  href={`https://${companyInfo.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#3B82F6" }}
                >
                  {companyInfo.website}
                </a>
              </div>
            </div>
          </div>
          <div>
            <h4 style={{ color: "#E5E7EB", marginBottom: "12px" }}>
              Description
            </h4>
            <div style={{ color: "#94A3B8", lineHeight: "1.6" }}>
              {companyInfo.description}
            </div>
          </div>
        </div>
      </div>

      {/* Trading Actions */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Trading Actions</h3>
        </div>
        <div className="grid-2">
          <button
            onClick={() =>
              navigate("/add-investment", {
                state: { symbol: stockData.symbol, name: stockData.name },
              })
            }
            className="btn btn-large"
          >
            💰 Buy {stockData.symbol}
          </button>
          <button
            onClick={() =>
              navigate("/add-transaction", {
                state: {
                  symbol: stockData.symbol,
                  name: stockData.name,
                  type: "sell",
                },
              })
            }
            className="btn btn-error btn-large"
          >
            📉 Sell {stockData.symbol}
          </button>
          <button
            onClick={() =>
              navigate("/add-transaction", {
                state: {
                  symbol: stockData.symbol,
                  name: stockData.name,
                  type: "buy",
                },
              })
            }
            className="btn btn-outline btn-large"
          >
            ➕ Add Transaction
          </button>
          <button
            onClick={() => navigate("/market")}
            className="btn btn-outline btn-large"
          >
            📊 View Market
          </button>
        </div>
      </div>

      {/* News Placeholder */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Latest News</h3>
        </div>
        <div
          style={{
            height: "200px",
            background: "#2A2A2A",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#94A3B8",
          }}
        >
          📰 Latest news about {stockData.symbol} will be displayed here
        </div>
      </div>

      <Navigation />
    </div>
  );
};

export default StockDetailScreen;
